




















import Vue from 'vue';
import {
  Component, Inject, Watch, Prop,
} from 'vue-property-decorator';
import { mapState } from 'vuex';
import {
  Alert, NoticeClass, AuthStore, Dialog,
} from '@kasasa/fbase-components/lib';
import {
  KNoticeContainer,
} from '@kasasa/fbase-components';
import { Cycle } from '@/api/cycles';
import {
  RewardsValidationResource, CycleValidationSnapshot, CycleTransactionCount, ValidationSnapshot,
} from '@/api/rewardsvalidation/';
import * as d3 from 'd3';

interface GraphData {
  cycle: Cycle,
  count: CycleTransactionCount | null
}

interface DiffLineDescription {
  xPos: number,
  yStart: number,
  yEnd: number,
  label: string,
  labelClass: string
}

@Component({
  computed: mapState(['fiId']),
})
export default class TransactionCountHistoryGraph extends Vue {
  @Prop() currentTransactionCount!: CycleTransactionCount;

  @Prop() transactionTypeName!: string;

  @Prop() cycles!: Cycle[];

  fiId!: string;

  loading: boolean = true;

  priorSnapshots: (ValidationSnapshot | null)[] = [];

  xScale!: any;

  yScale!: any;

  chartGroup!: any;

  yAxis: any;

  svg: any;

  // Internal size of the graph. Scaled to the size of the enclosing element
  readonly width: number = 1000;

  readonly height: number = 200;

  @Inject('RewardsValidationResource')
  rewardsValidationResource!: RewardsValidationResource;

  mounted() {
    this.loadPriorCycleData();
  }

  @Watch('selectedCycle')
  loadPriorCycleData() {
    this.loading = true;
    this.clearChart();

    this.priorSnapshots = [];

    if (this.cycles && this.cycles.length > 0) {
      this.loadPriorSnapshots();
    }
  }

  loadPriorSnapshots() {
    const promises: Promise<CycleValidationSnapshot>[] = [];

    for (let i = 0; i < this.cycles.length - 1; i++) {
      promises.push(this.rewardsValidationResource.getCycleValidationSnapshot(this.fiId, this.cycles[i].id));
    }

    Promise.allSettled(promises).then((results: PromiseSettledResult<CycleValidationSnapshot>[]) => {
      results.forEach((result: PromiseSettledResult<CycleValidationSnapshot>) => {
        if (result.status === 'fulfilled') {
          this.priorSnapshots.push(TransactionCountHistoryGraph.parseSnapshot(result.value));
        } else {
          console.log(result.reason);
          this.priorSnapshots.push(null);
        }
      });

      this.loading = false;
      this.initChart();
    });
  }

  static parseSnapshot(snapshot: CycleValidationSnapshot): ValidationSnapshot | null {
    // Newer snapshots are instances of ValidationSnapshot.  Older ones are just arrays of ValidationProduct.
    const parsedObj: any = JSON.parse(snapshot.jsonString);

    if (Array.isArray(parsedObj)) {
      // This is an old snapshot that does not contain transaction data. It's of no use to us.
      return null;
    }

    return parsedObj as ValidationSnapshot;
  }

  clearChart() {
    if (this.svg) {
      this.svg.selectAll('*').remove();
    }
  }

  initChart() {
    if (!this.svg) {
      this.svg = d3
        .select('#graph')
        .append('svg')
        .attr('preserveAspectRatio', 'xMinYMin meet')
        .attr('viewBox', `0 0 ${this.width} ${this.height}`)
        .classed('svg-content-responsive', true);
    }

    this.xScale = d3.scaleBand().range([0, this.width]).padding(0.4);
    this.yScale = d3.scaleLinear().range([this.height, 0]);

    // Horizontal domain is always the same (cycles), but vertical domain
    // may change when we switch to other transaction types.
    this.xScale.domain(this.cycles.map((cycle: Cycle) => cycle.id));

    this.chartGroup = this.svg.append('g');

    this.chartGroup.append('g')
      .attr('transform', `translate(0, ${this.height})`)
      .attr('class', 'xaxis')
      .call(d3.axisBottom(this.xScale)
        .tickSize(0)
        .tickFormat((value: any, index: number) => TransactionCountHistoryGraph.getDateTick(this.cycles[index])));

    this.yAxis = d3.axisLeft(this.yScale)
      .ticks(5)
      .tickSize(-this.width);

    this.chartGroup.append('g')
      .attr('class', 'yaxis');

    this.updateChart();
  }

  static getDateTick(cycle: Cycle): string {
    return `${TransactionCountHistoryGraph.formatAxisDate(cycle.qualificationStartDate)} - ${TransactionCountHistoryGraph.formatAxisDate(cycle.qualificationEndDate)}`;
  }

  static formatAxisDate(dateString: string): string {
    const parsedDate: Date = new Date(dateString);

    return `${(parsedDate.getUTCMonth() + 1).toLocaleString('en-US', {
      minimumIntegerDigits: 2,
    })}/${parsedDate.getUTCDate().toLocaleString('en-US', {
      minimumIntegerDigits: 2,
    })}`;
  }

  @Watch('currentTransactionCount')
  updateChart() {
    this.chartGroup.selectAll('.barContainer').remove();

    const graphData: GraphData[] = this.buildGraphData();

    let maxY: number = Math.max(...graphData.map((data: GraphData) => (data.count ? data.count.count : 0)));

    // Round up to get top of scale
    if (maxY < 100 && maxY > 10) {
      maxY = TransactionCountHistoryGraph.roundUpToNearest(maxY, 10);
    } else if (maxY < 1000) {
      maxY = TransactionCountHistoryGraph.roundUpToNearest(maxY, 100);
    } else {
      maxY = TransactionCountHistoryGraph.roundUpToNearest(maxY, 1000);
    }

    this.yScale.domain([0, maxY]);
    this.chartGroup.selectAll('g.yaxis')
      .call(this.yAxis)
      .select('.domain')
      .remove();

    const barGroup = this.chartGroup.selectAll('.bar')
      .data(graphData)
      .enter()
      .append('g')
      .attr('class', 'barContainer');

    this.createBars(barGroup);
    this.createBarLabels(barGroup);
    this.createDiffLines(barGroup, graphData);
  }

  buildGraphData(): GraphData[] {
    const graphData: GraphData[] = [];
    for (let i = 0; i < this.cycles.length - 1; i++) {
      const transactionCount: CycleTransactionCount | null = this.getCycleTransactionCountFromSnapshot(this.priorSnapshots[i]);

      graphData.push({
        cycle: this.cycles[i],
        count: transactionCount,
      });
    }

    // Last one should be the currently selected cycle (last in history) and count
    graphData.push({
      cycle: this.cycles[this.cycles.length - 1],
      count: this.currentTransactionCount,
    });

    return graphData;
  }

  static roundUpToNearest(toRound: number, toNearest: number): number {
    return (Math.ceil(toRound / toNearest) * toNearest);
  }

  createBars(barGroup: any) {
    const parent: TransactionCountHistoryGraph = this;
    barGroup.append('rect')
      .attr('class', (data: GraphData, index: number) => parent.getClassForBar(data, index))
      .attr('x', (data: GraphData, index: number) => parent.xScale(data.cycle.id))
      .attr('y', (value: GraphData) => parent.yScale(value.count ? value.count.count : 0))
      .attr('width', this.xScale.bandwidth())
      .attr('height', (value: GraphData) => parent.height - parent.yScale(value.count ? value.count.count : 0));
  }

  createBarLabels(barGroup: any) {
    const parent: TransactionCountHistoryGraph = this;
    barGroup
      .append('text')
      .attr('text-anchor', 'middle')
      .attr('x', (data: GraphData, index: number) => parent.xScale(data.cycle.id) + (parent.xScale.bandwidth() / 2))
      .attr('y', (data: GraphData) => parent.calculateBarLabelYPos(data))
      .attr('class', (data: GraphData) => {
        let textClass = 'barLabelText';
        const barTop = parent.yScale(data.count ? data.count.count : 0);
        // White text only if the bar is high enough that we'll put the label inside it
        if (barTop <= parent.height - 25) {
          textClass += ' whiteText';
        }
        return textClass;
      })
      .text((data: GraphData, index: number) => {
        if (data.count) {
          return data.count.count.toLocaleString();
        }
        return 'No data';
      });
  }

  createDiffLines(barGroup: any, graphData: GraphData[]) {
    const lines: DiffLineDescription[] = this.generateDiffLineDescriptions(graphData);

    // Main body of line
    barGroup
      .append('line')
      .style('stroke', 'black')
      .attr('x1', (data: GraphData, index: number) => lines[index].xPos)
      .attr('y1', (data: GraphData, index: number) => lines[index].yStart)
      .attr('x2', (data: GraphData, index: number) => lines[index].xPos)
      .attr('y2', (data: GraphData, index: number) => lines[index].yEnd);

    // Start cap
    barGroup
      .append('line')
      .style('stroke', 'black')
      .attr('x1', (data: GraphData, index: number) => (lines[index].xPos > 0 ? lines[index].xPos - 4 : 0))
      .attr('y1', (data: GraphData, index: number) => (lines[index].xPos > 0 ? lines[index].yStart : 0))
      .attr('x2', (data: GraphData, index: number) => (lines[index].xPos > 0 ? lines[index].xPos + 4 : 0))
      .attr('y2', (data: GraphData, index: number) => (lines[index].xPos > 0 ? lines[index].yStart : 0));

    // End cap
    barGroup
      .append('line')
      .style('stroke', 'black')
      .attr('x1', (data: GraphData, index: number) => (lines[index].xPos > 0 ? lines[index].xPos - 4 : 0))
      .attr('y1', (data: GraphData, index: number) => (lines[index].xPos > 0 ? lines[index].yEnd : 0))
      .attr('x2', (data: GraphData, index: number) => (lines[index].xPos > 0 ? lines[index].xPos + 4 : 0))
      .attr('y2', (data: GraphData, index: number) => (lines[index].xPos > 0 ? lines[index].yEnd : 0));

    // Label
    barGroup
      .append('text')
      .attr('text-anchor', 'middle')
      .attr('class', (data: GraphData, index: number) => lines[index].labelClass)
      .attr('x', (data: GraphData, index: number) => lines[index].xPos)
      .attr('y', (data: GraphData, index: number) => lines[index].yStart - 5)
      .text((data: GraphData, index: number) => lines[index].label);
  }

  generateDiffLineDescriptions(graphData: GraphData[]): DiffLineDescription[] {
    const descriptions: DiffLineDescription[] = [];

    for (let i = 0; i < graphData.length; i++) {
      if (i > 0 && graphData[i - 1].count) {
        descriptions.push(this.generateDiffLineDescription(graphData[i - 1], graphData[i], i));
      } else {
        descriptions.push({
          xPos: 0,
          yStart: 0,
          yEnd: 0,
          label: '',
          labelClass: '',
        });
      }
    }
    return descriptions;
  }

  generateDiffLineDescription(previous: GraphData, current: GraphData, index: number): DiffLineDescription {
    const xPos = this.xScale(current.cycle.id) - (this.xScale.bandwidth() / 3);
    let yStart = 0;
    let yEnd = 0;

    if (current.count!.count > previous.count!.count) {
      yStart = this.yScale(current.count!.count);
      yEnd = this.yScale(previous.count!.count);
    } else {
      yStart = this.yScale(previous.count!.count);
      yEnd = this.yScale(current.count!.count);
    }

    const label = TransactionCountHistoryGraph.calculateDiffPercentage(previous.count!.count, current.count!.count);
    let labelClass = 'diffText';

    if (index === this.cycles.length - 1 && current.count && current.count.yellowThresholdMinimum && current.count.yellowThresholdMaximum
      && current.count.greenThresholdMinimum && current.count.greenThresholdMaximum) {
      // Last one might be a different color depending on thresholds
      if (current.count.count < current.count.yellowThresholdMinimum || current.count.count > current.count.yellowThresholdMaximum) {
        labelClass += ' redBar';
      } else if (current.count.count < current.count.greenThresholdMinimum || current.count.count > current.count.greenThresholdMaximum) {
        labelClass += ' yellowBar';
      } else {
        labelClass += ' greenBar';
      }
    }

    return {
      xPos,
      yStart,
      yEnd,
      label,
      labelClass,
    };
  }

  static calculateDiffPercentage(previous: number, current: number): string {
    const percentage: number = ((current - previous) / previous) * 100;

    return `${percentage >= 0 ? '+' : ''}${+percentage.toFixed(2)}%`;
  }

  getCycleTransactionCountFromSnapshot(snapshot: ValidationSnapshot | null): CycleTransactionCount | null {
    let result: CycleTransactionCount | null = null;

    if (snapshot) {
      for (let i = 0; i < snapshot.transactionCounts.length; i++) {
        if (snapshot.transactionCounts[i].transactionType === this.currentTransactionCount.transactionType) {
          result = snapshot.transactionCounts[i];
          break;
        }
      }
    }

    return result;
  }

  getClassForBar(data: GraphData, index: number) {
    let barClass = 'bar';
    if (index === this.cycles.length - 1 && data.count && data.count.yellowThresholdMinimum && data.count.yellowThresholdMaximum
      && data.count.greenThresholdMinimum && data.count.greenThresholdMaximum) {
      if (data.count.count < data.count.yellowThresholdMinimum || data.count.count > data.count.yellowThresholdMaximum) {
        barClass += ' redBar';
      } else if (data.count.count < data.count.greenThresholdMinimum || data.count.count > data.count.greenThresholdMaximum) {
        barClass += ' yellowBar';
      } else {
        barClass += ' greenBar';
      }
    }
    return barClass;
  }

  calculateBarLabelYPos(data: GraphData): number {
    let yPos = 0;
    if (data.count) {
      const barTop = this.yScale(data.count ? data.count.count : 0);

      if (barTop > this.height - 25) {
        // Top of bar is too low, so put the label just above it.
        yPos = barTop - 2;
      } else {
        // Otherwise move the label inside the bar
        yPos = barTop + 10;
      }
    } else {
      // No data so place label just above x axis
      yPos = this.height - 2;
    }

    return yPos;
  }

  showErrorMessage(error: string) {
    console.error(error);
    const al = new Alert(error, NoticeClass.ERROR);
    al.setTimeout(5000);
    this.$store.dispatch('notices/add', al);
  }
}
